/**
 * V1 Session Survey Page
 * 
 * Public page for taking session surveys.
 * Supports anonymous responses if the survey allows it.
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ChevronLeft } from 'lucide-react';
import { V1PageRenderer, V1SurveyNavigation, type V1Survey, type V1QuestionResponse } from '@portal/components/v1/questionnaires/shared';
import { V1SessionSurveyConfirmation } from '../components/session_surveys/V1SessionSurveyConfirmation';
import { V1SurveyStatusCard } from '../components/surveys/V1SurveyStatusCard';
import { v1SurveysApi } from '../api/v1SurveysApi';
import { useEventContext } from '../../contexts/EventContext';
import { decodeHtmlEntities } from '../../../utils/htmlUtils';

export const SessionSurveyPageV1: React.FC = () => {
  const { daySlug, sessionSlug, surveySlug } = useParams<{ 
    daySlug: string;
    sessionSlug: string;
    surveySlug: string;
  }>();
  const navigate = useNavigate();
  const { eventSlug } = useEventContext();
  const queryClient = useQueryClient();
  
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [responses, setResponses] = useState<Record<string, V1QuestionResponse>>({});
  const [touchedQuestions, setTouchedQuestions] = useState<Set<string>>(new Set());
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [isSubmitted, setIsSubmitted] = useState(false);

  // Fetch survey data
  const { data: survey, isLoading, error } = useQuery({
    queryKey: ['v1-session-survey', eventSlug, daySlug, sessionSlug, surveySlug],
    queryFn: () => v1SurveysApi.getSessionSurvey(eventSlug!, daySlug!, sessionSlug!, surveySlug!),
  });

  // Submit survey mutation
  const submitMutation = useMutation({
    mutationFn: (responses: V1QuestionResponse[]) => 
      v1SurveysApi.submitSessionSurvey(eventSlug!, daySlug!, sessionSlug!, surveySlug!, responses),
    onSuccess: () => {
      // Invalidate the surveys list cache so it shows as completed
      queryClient.invalidateQueries({ queryKey: ['v1-surveys-list', eventSlug] });
      setIsSubmitted(true);
    },
    onError: (error) => {
      console.error('Survey submission error:', error);
    },
  });

  // Handle authentication errors - redirect to sign in if we get a 401
  useEffect(() => {
    if (error) {
      // Check if it's a StructuredApiError with 401 status
      const apiError = error as any;
      if (apiError.status === 401) {
        // Authentication required - redirect to sign in
        const returnUrl = encodeURIComponent(window.location.pathname);
        window.location.href = `${window.location.origin}/accounts/sign_in?return_to=${returnUrl}`;
      }
    }
  }, [error]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-500">Loading survey...</div>
      </div>
    );
  }

  if (error || !survey) {
    // Don't show error page for auth errors - the redirect will handle it
    if (error && (error as any).status === 401) {
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-gray-500">Redirecting to sign in...</div>
        </div>
      );
    }
    
    // Handle different error scenarios
    if (error && (error as any).status === 403) {
      // Check the error message to determine the specific issue
      const errorMessage = (error as any).errors?.base?.[0] || (error as any).message || '';
      
      if (errorMessage.includes('already completed')) {
        return (
          <V1SurveyStatusCard
            title="Survey Already Completed"
            message="Thank you! You have already completed this survey."
            status="info"
            eventSlug={eventSlug!}
            showSurveysButton={true}
            showScheduleButton={true}
            isSessionSurvey={true}
          />
        );
      } else if (errorMessage.includes('not yet open')) {
        return (
          <V1SurveyStatusCard
            title="Survey Not Yet Available"
            message="This survey is not yet open. Please check back later."
            status="warning"
            eventSlug={eventSlug!}
            showSurveysButton={true}
            showScheduleButton={true}
            isSessionSurvey={true}
          />
        );
      } else if (errorMessage.includes('has closed')) {
        return (
          <V1SurveyStatusCard
            title="Survey Closed"
            message="This survey has closed and is no longer accepting responses."
            status="warning"
            eventSlug={eventSlug!}
            showSurveysButton={true}
            showScheduleButton={true}
            isSessionSurvey={true}
          />
        );
      }
    }
    
    // Default error for survey not found
    return (
      <V1SurveyStatusCard
        title="Survey Not Found"
        message="This survey may no longer be available."
        status="error"
        eventSlug={eventSlug!}
        showSurveysButton={true}
        showScheduleButton={true}
        isSessionSurvey={true}
      />
    );
  }

  if (isSubmitted) {
    return <V1SessionSurveyConfirmation 
      surveyName={survey.name}
      sessionName={survey.surveyableName || 'Session'}
      eventSlug={eventSlug!}
    />;
  }

  const currentPage = survey.pages[currentPageIndex];
  const totalPages = survey.pages.length;

  const handleQuestionChange = (questionId: string, response: V1QuestionResponse) => {
    setResponses(prev => ({
      ...prev,
      [questionId]: response,
    }));
    
    // Clear validation error when user changes the answer
    if (validationErrors[questionId]) {
      setValidationErrors(prev => {
        const next = { ...prev };
        delete next[questionId];
        return next;
      });
    }
  };

  const handleQuestionBlur = (questionId: string) => {
    setTouchedQuestions(prev => new Set(prev).add(questionId));
  };

  const validateCurrentPage = (): boolean => {
    const errors: Record<string, string> = {};
    let isValid = true;

    currentPage.questions.forEach(question => {
      const response = responses[question.id];
      
      // Check required fields
      if (question.required) {
        // Check if question has any answer options (radio/checkbox)
        const hasAnswerOptions = question.options && question.options.length > 0;
        
        // For questions with only a comment box (no star rating, no answer options),
        // accept the comment as a valid response
        const needsAnswerOptions = ['radio', 'checkbox', 'select'].includes(question.type);
        const isCommentOnlyQuestion = !question.hasStarRating && 
                                      question.hasCommentBox && 
                                      (!needsAnswerOptions || !hasAnswerOptions);
        
        const hasValue = response?.value || 
                        response?.answerId || 
                        response?.answerIds?.length || 
                        response?.starRating ||
                        (isCommentOnlyQuestion && response?.comment && response.comment.trim() !== '');
                        
        if (!hasValue) {
          errors[question.id] = 'This question is required';
          isValid = false;
        }
      }
      
      // Check comment required
      if (question.hasCommentBox && question.commentRequired && !response?.comment) {
        errors[question.id] = 'Comment is required';
        isValid = false;
      }
      
      // Check min length
      if (question.validations?.minLength && response?.value) {
        const value = String(response.value);
        if (value.length < question.validations.minLength) {
          errors[question.id] = `Minimum ${question.validations.minLength} characters required`;
          isValid = false;
        }
      }
    });

    setValidationErrors(errors);
    
    // Mark all questions on current page as touched
    currentPage.questions.forEach(q => {
      setTouchedQuestions(prev => new Set(prev).add(q.id));
    });

    return isValid;
  };

  const handleNext = () => {
    if (validateCurrentPage()) {
      setCurrentPageIndex(prev => Math.min(prev + 1, totalPages - 1));
      window.scrollTo(0, 0);
    }
  };

  const handlePrevious = () => {
    setCurrentPageIndex(prev => Math.max(prev - 1, 0));
    window.scrollTo(0, 0);
  };

  const handleSubmit = () => {
    if (validateCurrentPage()) {
      // Convert responses to array format for nested attributes
      const responseArray = Object.values(responses).map(r => {
        const formattedResponse: any = {
          questionId: r.questionId,
        };
        
        // Only include fields that have values
        if (r.answerId) formattedResponse.answerId = r.answerId;
        if (r.answerIds?.length) formattedResponse.answerIds = r.answerIds;
        if (r.starRating) formattedResponse.starRating = r.starRating;
        
        // Only include response field for text values or comments, not for radio/checkbox
        if (r.comment || (r.value && !r.answerId && !r.answerIds)) {
          formattedResponse.response = r.comment || r.value;
        }
        
        return formattedResponse;
      });
      console.log('Submitting survey responses:', responseArray);
      submitMutation.mutate(responseArray);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate(`/schedule`)}
            className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-4"
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Back to schedule
          </button>
          
          <h1 className="text-2xl font-bold text-gray-900">{survey.name}</h1>
          {survey.surveyableName && (
            <p className="text-lg text-gray-700 mt-1">Session: {survey.surveyableName}</p>
          )}
          {survey.description && (
            <p 
              className="mt-2 text-gray-600"
              dangerouslySetInnerHTML={{ __html: survey.description }}
            />
          )}
          
          {/* Anonymous indicator */}
          {survey.anonymous && (
            <p className="mt-2 text-sm text-gray-500">
              Your responses to this survey are anonymous and cannot be linked to your account.
            </p>
          )}
        </div>

        {/* Survey content */}
        <V1PageRenderer
          page={currentPage}
          responses={responses}
          validationErrors={validationErrors}
          touchedQuestions={touchedQuestions}
          onChange={handleQuestionChange}
          onBlur={handleQuestionBlur}
          primaryColor="#3B82F6"
        />

        {/* Navigation */}
        <V1SurveyNavigation
          currentPage={currentPageIndex + 1}
          totalPages={totalPages}
          onPrevious={handlePrevious}
          onNext={handleNext}
          onSubmit={handleSubmit}
          isSubmitting={submitMutation.isPending}
          canGoBack={currentPageIndex > 0}
          canGoForward={currentPageIndex < totalPages - 1}
          isLastPage={currentPageIndex === totalPages - 1}
        />
      </div>
    </div>
  );
};